import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getDisplayName, endSession, getSsoIdToken } from '../auth/session';
import authConfig, { isConfigured, logoutUri } from '../config/authConfig';
import './Navbar.css';

/* ── Nav icons ─────────────────────────────────────────────── */
function DashboardIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
      <rect x="3" y="3" width="8" height="8" rx="1.5" opacity="0.9" />
      <rect x="13" y="3" width="8" height="8" rx="1.5" opacity="0.6" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" opacity="0.6" />
      <rect x="13" y="13" width="8" height="8" rx="1.5" opacity="0.9" />
    </svg>
  );
}

function AnalyticsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6"  y1="20" x2="6"  y2="14" />
      <line x1="2"  y1="20" x2="22" y2="20" />
    </svg>
  );
}

function TransactionsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

function IncidentsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function AlertsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ReviewsIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="9" y1="10" x2="15" y2="10" />
      <line x1="9" y1="14" x2="13" y2="14" />
    </svg>
  );
}

function FinanceIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <path d="M7 10h2M15 10h2M11 10h2" />
    </svg>
  );
}

function GithubIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
    </svg>
  );
}


function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/**
 * Vertical sidebar navigation shown on all protected pages.
 */
function Navbar() {
  const navigate  = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const username  = getDisplayName() || 'User';

  /**
   * Signs the user out.
   *
   * Local tokens are cleared FIRST, before any redirect. If the redirect fails —
   * network, a Cognito outage, a browser blocking it — the session is already
   * gone locally, which is the outcome the user asked for. Redirecting first and
   * clearing after would leave a live session behind on any failure.
   *
   * An SSO session then goes on to Cognito's /logout to end the Cognito session
   * too. That endpoint is not OIDC-standard — it takes client_id and logout_uri
   * rather than id_token_hint and post_logout_redirect_uri — which is why this
   * builds the URL rather than calling signoutRedirect().
   *
   * Note this ends the Cognito session, NOT the Microsoft one. The user stays
   * signed in to Microsoft, so signing back in may not prompt for credentials.
   * That is normal SSO behaviour but it surprises people, which is why the
   * button says "Sign Out" and does not promise more than it does.
   */
  const handleLogout = () => {
    const wasSso = Boolean(getSsoIdToken());

    endSession();

    if (wasSso && isConfigured()) {
      const params = new URLSearchParams({
        client_id: authConfig.clientId,
        logout_uri: logoutUri(),
      });
      window.location.assign(`${authConfig.logoutUrl}?${params}`);
      return;
    }

    navigate('/login');
  };

  return (
    <nav className="sidebar">
      {/* Brand */}
      <div className="sidebar-brand">
        <img src="/command_quality_pty_ltd_logo.jpg" alt="Command Quality" className="sidebar-logo" />
        <span className="sidebar-title">Developer Tool</span>
      </div>

      {/* Navigation links */}
      <div className="sidebar-links">
        <NavLink to="/dashboard" end className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <DashboardIcon />
          <span>Dashboard</span>
        </NavLink>
        <NavLink to="/dashboard-v2" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <AnalyticsIcon />
          <span>Analytics</span>
        </NavLink>
        <NavLink to="/business-transactions" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <TransactionsIcon />
          <span>Transactions</span>
        </NavLink>
        <NavLink to="/alerts" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <AlertsIcon />
          <span>Alerts</span>
        </NavLink>
        <NavLink to="/incidents" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <IncidentsIcon />
          <span>Incidents</span>
        </NavLink>
        <NavLink to="/post-incident-reviews" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <ReviewsIcon />
          <span>PIR</span>
        </NavLink>
        <NavLink to="/github-prs" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <GithubIcon />
          <span>GitHub PRs</span>
        </NavLink>

        {/* Finance section — commented out pending finance module rollout
        <div className="sidebar-section-label">Finance</div>
        <NavLink to="/finance/dashboard" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <FinanceIcon />
          <span>Finance Dashboard</span>
        </NavLink>
        <NavLink to="/finance/records" className={({ isActive }) => isActive ? 'sidebar-link active' : 'sidebar-link'}>
          <FinanceIcon />
          <span>Finance Records</span>
        </NavLink>
        */}
      </div>

      {/* Bottom section: theme toggle, user, logout */}
      <div className="sidebar-footer">
        <button className="theme-toggle" onClick={toggleTheme} title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
          {theme === 'light' ? <MoonIcon /> : <SunIcon />}
        </button>

        <div className="sidebar-user-info">
          <div className="sidebar-user-avatar" aria-hidden="true">
            {username.charAt(0)}
          </div>
          <span className="sidebar-username">{username}</span>
        </div>

        <button className="btn-logout" onClick={handleLogout}>Sign Out</button>
      </div>
    </nav>
  );
}

export default Navbar;
